// AI秘書パネル。ヘッダ＋本体（履歴一覧/チャット/睡眠）を状態で切り替える。
//
// 表示形態は2つ:
//   docked（既定）= 右端から画面幅1/4のパネルがスライドイン。左端を掴んで幅を伸縮できる
//   popout        = 浮動ウインドウ。ヘッダを掴んで移動、右下の角でサイズ変更が可能
// どちらでも最小化（ヘッダだけ残す）ができる。
import { useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useSecretary } from '../hooks/useSecretary';
import type { UseSecretaryResult } from '../hooks/useSecretary';
import { useDrag } from '../hooks/useDrag';
import { useEdgeResize } from '../hooks/useEdgeResize';
import { ChatView } from './ChatView';
import { HistoryView } from './HistoryView';
import { PanelHeader } from './PanelHeader';
import { SleepingView } from './SleepingView';
import type { PanelMode } from '../types';

interface SecretaryPanelProps {
  onClose: () => void;
}

function containerClass(mode: PanelMode, isMinimized: boolean): string {
  const base = 'flex flex-col overflow-hidden border border-gray-200 bg-white shadow-2xl';
  if (mode === 'docked') {
    // 右端スライドパネル。既定幅は画面の1/4（最低20rem）。左端ハンドルの伸縮が優先される。
    return `${base} fixed right-0 top-0 w-[max(20rem,25vw)] animate-[secretary-slide-in_0.2s_ease-out] ${
      isMinimized ? 'rounded-bl-2xl' : 'h-full border-l'
    }`;
  }
  // 浮動ウインドウ。resize は CSS ネイティブのサイズ変更（右下の角を掴んで伸縮）。
  return `${base} rounded-2xl ${
    isMinimized ? 'w-80' : 'resize h-[28rem] w-96 min-h-[16rem] min-w-[18rem] max-h-[90vh] max-w-[90vw]'
  }`;
}

/** ドック時の左端リサイズハンドル。掴んで左右に動かすと幅が変わる。 */
function EdgeHandle(props: { onPointerDown: (e: ReactPointerEvent) => void }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="幅を変更"
      onPointerDown={props.onPointerDown}
      className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-ew-resize touch-none transition hover:bg-blue-400/50"
    />
  );
}

/** パネル本体。履歴一覧 → チャット → 睡眠表示の優先順で出し分ける。 */
function PanelBody(props: {
  secretary: UseSecretaryResult;
  showHistory: boolean;
  onPicked: () => void;
}) {
  const { secretary, showHistory, onPicked } = props;
  if (showHistory) {
    return (
      <HistoryView
        sessions={secretary.sessions}
        currentId={secretary.currentId}
        onSelect={(id) => {
          secretary.selectSession(id);
          onPicked();
        }}
        onDelete={secretary.deleteSession}
      />
    );
  }
  if (secretary.status !== 'ready') return <SleepingView status={secretary.status} />;
  return <ChatView secretary={secretary} />;
}

export function SecretaryPanel({ onClose }: SecretaryPanelProps) {
  const secretary = useSecretary(true);
  const drag = useDrag();
  const edge = useEdgeResize(drag.ref);
  const [mode, setMode] = useState<PanelMode>('docked');
  const [isMinimized, setIsMinimized] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // リサイズはインラインの width/height を書き込むため、表示形態が変わるときに
  // 消しておかないとサイズが引き継がれて崩れる。最小化では width を残す
  // （ユーザーが広げたドック幅を、最小化→復元で失わせないため）。
  const clearInlineSize = (clearWidth: boolean) => {
    const el = drag.ref.current;
    if (!el) return;
    el.style.height = '';
    if (clearWidth) el.style.width = '';
  };

  const togglePopout = () => {
    clearInlineSize(true);
    if (mode === 'docked' && !drag.pos) {
      // 初回ポップアウトは右下寄りへ出す（2回目以降は前回の位置を覚えている）
      drag.moveTo(Math.max(window.innerWidth - 420, 16), Math.max(window.innerHeight - 500, 16));
    }
    setMode((m) => (m === 'docked' ? 'popout' : 'docked'));
  };

  const toggleMinimize = () => {
    clearInlineSize(false);
    setIsMinimized((v) => !v);
  };

  return (
    <div ref={drag.ref} style={mode === 'popout' ? drag.style : undefined} className={containerClass(mode, isMinimized)}>
      {mode === 'docked' && !isMinimized && <EdgeHandle onPointerDown={edge.onPointerDown} />}
      <PanelHeader
        model={secretary.model}
        mode={mode}
        isMinimized={isMinimized}
        onToggleHistory={() => setShowHistory((v) => !v)}
        onNewSession={() => {
          secretary.newSession();
          setShowHistory(false); // 新しい会話はすぐ入力できるようチャット画面へ
        }}
        onTogglePopout={togglePopout}
        onToggleMinimize={toggleMinimize}
        onClose={onClose}
        onPointerDown={mode === 'popout' ? drag.onPointerDown : undefined}
      />
      {!isMinimized && (
        <PanelBody
          secretary={secretary}
          showHistory={showHistory}
          onPicked={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
